import { useQuery } from '@tanstack/react-query';
import { describePasswordRules, type PasswordRules } from '@hrms/shared';
import { get } from '@/lib/api-client';

/**
 * The live password rules, as a sentence for the forms that must state them.
 *
 * Read from the server rather than written into the page: the rules live in
 * `security.policy` and are editable from Settings, so anything hard-coded
 * starts lying the moment somebody changes one.
 *
 * Returns `undefined` while loading and if the request fails. A form that
 * cannot reach the policy shows no hint at all — silence is better than a rule
 * the server will not actually enforce, which is the failure this replaces.
 * `retry: false` keeps a failure quiet and quick; the hint is not worth
 * hammering an endpoint the user does not need to reach the form.
 */
export function usePasswordRulesHint(): string | undefined {
  const { data } = useQuery({
    queryKey: ['auth', 'password-policy'],
    queryFn: () => get<PasswordRules>('/auth/password-policy'),
    // The policy changes about as often as somebody visits Settings.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return data ? describePasswordRules(data) : undefined;
}
